import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, Badge } from '../components/ui';
import { api } from '../lib/api';
import CallInGhl from '../components/CallInGhl';

interface LeadFlag {
  id: string;
  entity_type: 'opportunity' | 'contact';
  entity_id: string;
  apex_contact_id: string | null;
  signal: string;
  severity: 'high' | 'medium' | 'low';
  reason: string;
  suggested_next_step: string;
  entity_name: string | null;
  monetary_value: number | null;
  status: string;
  flagged_at: string;
  /** PHONE1 - the Call button's state, from GET /api/ghl-sync/lead-flags. */
  ghl_contact_url?: string | null;
  can_add_to_ghl?: boolean;
  ghl_link_unread?: boolean;
}

const money = (n: number | null) =>
  n == null ? null : n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

const humanizeSignal = (signal: string): string => {
  const map: Record<string, string> = {
    stale_opportunity: 'Stale opportunity',
    replied_no_followup: 'Replied — needs follow-up',
    no_activity: 'No recent activity',
    long_stale: 'Long stale',
    high_value_stale: 'High-value deal stalled',
    no_contact: 'No contact on record',
  };
  return map[signal] ?? signal.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const severityBadgeClass = (severity: string): string => {
  if (severity === 'high') return 'bg-red-100 text-red-700';
  if (severity === 'medium') return 'bg-warning-wash text-warning';
  return 'bg-gray-100 text-gray-600';
};

const severityLabel = (severity: string): string =>
  severity.charAt(0).toUpperCase() + severity.slice(1);

export default function NeedsAttention() {
  const [flags, setFlags] = useState<LeadFlag[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [severity, setSeverity] = useState('');
  const [dismissingId, setDismissingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchFlags = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = { status: 'open' };
      if (severity) params.severity = severity;
      const res = await api.listLeadFlags(params);
      setFlags(res.flags || []);
      setTotal(res.total || 0);
    } catch (err: any) {
      setError(err.message || 'Failed to load flags');
    } finally {
      setLoading(false);
    }
  }, [severity]);

  useEffect(() => { fetchFlags(); }, [fetchFlags]);

  const handleRefresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      await api.refreshLeadFlags();
      await fetchFlags();
    } catch (err: any) {
      setError(err.message || 'Refresh failed');
    } finally {
      setRefreshing(false);
    }
  };

  const handleDismiss = async (id: string) => {
    setDismissingId(id);
    try {
      await api.dismissLeadFlag(id);
      setFlags((prev) => prev.filter((f) => f.id !== id));
      setTotal((prev) => Math.max(0, prev - 1));
    } catch (err: any) {
      setError(err.message || 'Dismiss failed');
    } finally {
      setDismissingId(null);
    }
  };

  return (
    <Layout>
      {/* Page header */}
      <div className="mb-4">
        <div className="pb-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="font-display text-[28px] font-semibold uppercase leading-[1.06] tracking-[-.01em] text-gray-900 sm:text-[32px]">Needs Attention</h1>
              <p className="text-gray-500 mt-1 text-sm">
                {total > 0 ? `${total} lead${total === 1 ? '' : 's'} flagged` : 'No open flags'} ·{' '}
                AI surfaces at-risk deals — it never contacts anyone for you.
              </p>
            </div>

            <div className="flex items-center gap-3 flex-shrink-0">
              {/* Severity filter */}
              <select
                value={severity}
                onChange={(e) => setSeverity(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 bg-white"
              >
                <option value="">All severities</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>

              {/* Refresh button — re-runs AI analysis, has no outbound action */}
              <button
                onClick={handleRefresh}
                disabled={refreshing || loading}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-lg hover:bg-primary-600 disabled:opacity-60 transition-colors"
              >
                <svg
                  className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                {refreshing ? 'Analyzing…' : 'Refresh'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="mb-6 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Loading state */}
      {loading ? (
        <div className="text-center py-16 text-gray-500">Loading…</div>
      ) : flags.length === 0 ? (
        /* Empty state */
        <div className="text-center py-16">
          <div className="text-4xl mb-3">🎉</div>
          <p className="text-lg font-medium text-gray-700">Nothing needs attention right now</p>
          <p className="text-sm text-gray-500 mt-1">
            All leads are in good shape. Run Refresh to re-analyze.
          </p>
        </div>
      ) : (
        /* Flag cards */
        <div className="space-y-4">
          {flags.map((flag) => (
            <Card key={flag.id}>
              <CardContent className="p-5">
                {/* Top row: severity badge + name + signal + value */}
                <div className="flex flex-wrap items-start gap-3 mb-3">
                  <span className={`inline-flex items-center font-medium rounded-full px-2.5 py-0.5 text-xs ${severityBadgeClass(flag.severity)}`}>
                    {severityLabel(flag.severity)}
                  </span>

                  <div className="flex-1 min-w-0">
                    <span className="font-semibold text-gray-900 mr-2">
                      {flag.entity_name || flag.entity_id}
                    </span>
                    <Badge variant="default" size="sm">
                      {humanizeSignal(flag.signal)}
                    </Badge>
                  </div>

                  {flag.monetary_value != null && (
                    <span className="text-sm font-semibold text-gray-700 flex-shrink-0">
                      {money(flag.monetary_value)}
                    </span>
                  )}
                </div>

                {/* Reason */}
                <p className="text-sm text-gray-500 mb-3">{flag.reason}</p>

                {/* Suggested next step — advisory only, no send/outbound capability */}
                {flag.suggested_next_step && (
                  <div className="bg-primary-50 border border-primary-100 rounded-lg p-3 mb-4">
                    <p className="text-xs font-semibold text-primary-500 uppercase tracking-wide mb-1">
                      Suggested next step
                    </p>
                    <p className="text-sm text-primary-900">{flag.suggested_next_step}</p>
                  </div>
                )}

                {/* Action row: View contact, Call in GHL (PHONE1 - opens GoHighLevel's dialer, never dials by itself), Dismiss */}
                <div className="flex items-center gap-3 pt-1">
                  {flag.apex_contact_id && (
                    <Link
                      to={`/crm/contacts/${flag.apex_contact_id}`}
                      className="text-sm font-medium text-primary-500 hover:text-primary-600 underline-offset-2 hover:underline"
                    >
                      View contact
                    </Link>
                  )}
                  <CallInGhl url={flag.ghl_contact_url} contactId={flag.apex_contact_id} canAdd={flag.can_add_to_ghl} unread={flag.ghl_link_unread} />

                  <button
                    onClick={() => handleDismiss(flag.id)}
                    disabled={dismissingId === flag.id}
                    className="text-sm text-gray-500 hover:text-gray-700 disabled:opacity-50 transition-colors ml-auto"
                  >
                    {dismissingId === flag.id ? 'Dismissing…' : 'Dismiss'}
                  </button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </Layout>
  );
}
